import { cleanReferrerHost, REFERRER_HOST_PARAM } from '@bookkeeper/shared/first-touch-utm';
import type { Locale } from '@/i18n/i18n';
import { buildSignupUrl } from '@/lib/utm';

const APP_URL = 'https://app.bookeper.pl';
export type SignupEntityType = 'jdg' | 'biuro';

export function inferSignupEntityType(pathname: string): SignupEntityType {
  return pathname === '/biura' || pathname.endsWith('/biura') ? 'biuro' : 'jdg';
}

export function signupEntityTypeFromSearch(search: string): SignupEntityType | undefined {
  const value = new URLSearchParams(search).get('entity_type');
  return value === 'biuro' || value === 'jdg' ? value : undefined;
}

function explicitRouteSignupEntityType(pathname: string): SignupEntityType | undefined {
  if (pathname === '/biura' || pathname.endsWith('/biura')) {
    return 'biuro';
  }
  if (pathname === '/jdg' || pathname.endsWith('/jdg')) {
    return 'jdg';
  }
  return undefined;
}

export function resolveSignupEntityType(
  pathname: string,
  search: string,
  options: { entityType?: SignupEntityType } = {}
): SignupEntityType {
  return (
    options.entityType ??
    explicitRouteSignupEntityType(pathname) ??
    signupEntityTypeFromSearch(search) ??
    inferSignupEntityType(pathname)
  );
}

function localizeAuthPath(path: string, locale: Locale): string {
  if (locale === 'pl') {
    return path;
  }

  return `/${locale}${path}`;
}

function buildAuthUrl(path: string, locale: Locale): string {
  return `${APP_URL}${localizeAuthPath(path, locale)}`;
}

export function buildSignInUrl(locale: Locale): string {
  return buildAuthUrl('/auth/login', locale);
}

function appendEntityType(url: string, entityType?: SignupEntityType): string {
  if (!entityType) {
    return url;
  }

  const nextUrl = new URL(url, APP_URL);
  nextUrl.searchParams.set('entity_type', entityType);
  return nextUrl.toString();
}

/**
 * `search: null` builds the URL without attribution (no query, no first-touch
 * storage read); `firstTouch: false` keeps the query's own UTMs but reads no storage.
 */
export function buildSignUpUrl(
  locale: Locale,
  search?: string | null,
  options: {
    entityType?: SignupEntityType;
    source?: string;
    /** Slot within the page; read by the app's `readSignUpAttribution()` next to `signup_source`. */
    placement?: string;
    /** Page slug (`home`); same reader. */
    page?: string;
    firstTouch?: boolean;
  } = {}
): string {
  const base = buildAuthUrl('/auth/sign-up', locale);
  const attributed =
    search === null ? base : buildSignupUrl(base, search, { firstTouch: options.firstTouch });
  const url = new URL(appendEntityType(attributed, options.entityType));
  if (options.source) url.searchParams.set('signup_source', options.source);
  if (options.placement) url.searchParams.set('placement', options.placement);
  if (options.page) url.searchParams.set('page', options.page);
  return url.toString();
}

export const HANDOFF_DISTINCT_ID_PARAM = 'ph_did';
export const HANDOFF_SESSION_ID_PARAM = 'ph_sid';

/** True for the app's signup URL in any locale (`/auth/sign-up`, `/en/auth/sign-up`). */
export function isAppSignupUrl(href: string): boolean {
  try {
    const url = new URL(href, APP_URL);
    return url.origin === APP_URL && /^\/(?:[a-z]{2}\/)?auth\/sign-up\/?$/.test(url.pathname);
  } catch {
    return false;
  }
}

/**
 * Adds the PostHog ids the app bootstraps from to a signup URL, replacing any it
 * already carries. The one place the hand-off param names live. `ph_sid` goes on
 * whenever a session id exists (any consent state); `ph_did` only when the
 * caller has an accepted visitor's id, and never a `$`-prefixed sentinel.
 */
export function appendPosthogHandoff(
  url: string,
  ids: { distinctId: string | null; sessionId: string | null } | null,
  referrerHost?: string | null
): string {
  if (!isAppSignupUrl(url)) return url;
  const distinctId = ids?.distinctId && !ids.distinctId.startsWith('$') ? ids.distinctId : null;
  const sessionId = ids?.sessionId ?? null;
  // Host only, validated: the visitor's external referrer, which the app cannot see once www is
  // the referrer. Replaces any `ref_host` the link already carries (a pasted one is not ours).
  const referrer = cleanReferrerHost(referrerHost);
  if (!distinctId && !sessionId && !referrer) return url;
  const next = new URL(url, APP_URL);
  next.searchParams.delete(HANDOFF_DISTINCT_ID_PARAM);
  next.searchParams.delete(HANDOFF_SESSION_ID_PARAM);
  next.searchParams.delete(REFERRER_HOST_PARAM);
  if (distinctId) next.searchParams.set(HANDOFF_DISTINCT_ID_PARAM, distinctId);
  if (sessionId) next.searchParams.set(HANDOFF_SESSION_ID_PARAM, sessionId);
  if (referrer) next.searchParams.set(REFERRER_HOST_PARAM, referrer);
  return next.toString();
}

// Tags an internal landing path with the audience entity_type so cross-spoke
// links (e.g. /jdg → /ksef-automatycznie) keep signup attribution. Unlike
// appendEntityType above, this works on relative router paths, not app URLs.
export function withAudienceEntityType(path: string, entityType: SignupEntityType): string {
  const [pathname, query = ''] = path.split(/\?(.*)/s);
  const params = new URLSearchParams(query);
  params.set('entity_type', entityType);
  return `${pathname}?${params.toString()}`;
}

// Drops a carried audience entity_type from an internal landing path while
// preserving UTMs — for audience-neutral spokes (e.g. /wdrozenia) whose
// cross-links must not forward a stale jdg/biuro signup classification.
export function withoutAudienceEntityType(path: string): string {
  const [pathname, query = ''] = path.split(/\?(.*)/s);
  const params = new URLSearchParams(query);
  params.delete('entity_type');
  const nextQuery = params.toString();
  return nextQuery ? `${pathname}?${nextQuery}` : pathname;
}
